/**
 * Change history, inline on the tab — the compact list form of
 * `changeHistory[]` (newest first): one line per save with a summary, who and
 * when, and a "details" toggle that expands the same per-row old → new
 * readout the drawer renders. The initial entry (the loan's pre-workbook DSCR,
 * captured once on the first save) stays pinned at the bottom as the baseline.
 */
import { useMemo, useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { slate, teal } from "../../../../theme/colors";
import { EntryDetail, entrySummary, historyWhen } from "./DscrChangeHistoryDrawer";
import { listLoanUnits } from "./constants";

const fmtDscr = (v) =>
  v == null || Number.isNaN(Number(v)) ? "—" : `${Number(v).toFixed(2)}×`;

const Row = ({ dot, left, right, children }) => (
  <Box sx={{ py: 1.25, borderBottom: `1px dashed ${slate[200]}`, "&:last-of-type": { borderBottom: "none" } }}>
    <Flx ac jb g={2}>
      <Flx ac g={1.5} sx={{ minWidth: 0 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: dot, flexShrink: 0 }} />
        {left}
      </Flx>
      <Txt sx={{ fontSize: 11.5, color: slate[500], whiteSpace: "nowrap" }}>{right}</Txt>
    </Flx>
    {children}
  </Box>
);

Row.propTypes = { dot: PropTypes.string, left: PropTypes.node, right: PropTypes.node, children: PropTypes.node };

const DscrChangeHistoryList = ({ history, initialEntry, loan }) => {
  const [open, setOpen] = useState(null);
  const entries = useMemo(
    () => [...(Array.isArray(history) ? history : [])].reverse(),
    [history],
  );
  // Resolvers for ids in a diff: document ids → names, unit ids → numbers.
  const ctx = useMemo(
    () => ({
      docsById: new Map(
        (loan?.loanDocuments || []).map((d) => [
          String(d._id),
          d.file_display_name || d.fileName || d.docType || String(d._id),
        ]),
      ),
      unitsById: new Map(listLoanUnits(loan).map((u) => [String(u.unitId), u])),
    }),
    [loan],
  );

  if (!entries.length && !initialEntry) {
    return (
      <Txt sx={{ display: "block", fontSize: 12.5, color: slate[500], px: 0.5, py: 1 }}>
        No changes recorded yet — every save that changes a row lands here with who, when and the before → after.
      </Txt>
    );
  }

  return (
    <Box sx={{ px: 0.5 }}>
      {entries.map((e, i) => {
        const isOpen = open === i;
        return (
          <Row
            key={`${e?.changedDateTime || ""}-${i}`}
            dot={teal[500]}
            left={
              <Flx ac g={0.75}>
                <Txt sx={{ fontSize: 13, fontWeight: 700, color: slate[900] }}>{entrySummary(e?.changes)}</Txt>
                <Txt
                  onClick={() => setOpen(isOpen ? null : i)}
                  sx={{ fontSize: 12, color: teal[700], cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                >
                  {isOpen ? "hide" : "details"}
                </Txt>
              </Flx>
            }
            right={`${e?.changedBy || "—"} · ${historyWhen(e?.changedDateTime)}`}
          >
            {isOpen ? (
              <Box sx={{ mt: 1.25, ml: 2.5, pl: 1.5, borderLeft: `2px solid ${slate[100]}` }}>
                <EntryDetail entry={e} ctx={ctx} />
              </Box>
            ) : null}
          </Row>
        );
      })}
      {initialEntry ? (
        <Row
          dot={slate[400]}
          left={
            <Txt sx={{ fontSize: 13, color: slate[900] }}>
              <b>Initial entry</b> — the loan's pre-workbook DSCR, pinned as the baseline
            </Txt>
          }
          right={historyWhen(initialEntry.capturedAt)}
        >
          <Txt sx={{ display: "block", fontSize: 12, color: slate[600], mt: 0.5, ml: 2.5 }}>
            DSCR at creation <b>{fmtDscr(initialEntry.dscr)}</b>
          </Txt>
        </Row>
      ) : null}
    </Box>
  );
};

DscrChangeHistoryList.propTypes = {
  history: PropTypes.array,
  initialEntry: PropTypes.object,
  loan: PropTypes.object,
};

export default DscrChangeHistoryList;
